import { useState, useMemo } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Shield, X } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

// Centralised admin audit log. Read-only by design — entries are
// written server-side by the worker's `logAudit()` helper on every
// instrumented mutation. No edit / delete affordance; if a row is
// wrong the underlying mutation needs to be re-issued, not the log
// edited (otherwise the trail loses integrity).
//
// Pagination: cursor-based on `created_at`. The server returns
// `next_cursor` (oldest entry's created_at) or null when exhausted.

type AuditEntry = {
  id: string
  admin_id: string | null
  admin_name: string | null
  admin_email: string | null
  action: string
  entity_type: string | null
  entity_id: string | null
  before: unknown
  after: unknown
  ip: string | null
  user_agent: string | null
  created_at: string
}

type AuditPage = {
  entries: AuditEntry[]
  next_cursor: string | null
}

export default function AuditLog() {
  const [adminFilter, setAdminFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [entityTypeFilter, setEntityTypeFilter] = useState('')
  const [entityIdFilter, setEntityIdFilter] = useState('')
  const [detail, setDetail] = useState<AuditEntry | null>(null)

  const params = useMemo(() => {
    const q = new URLSearchParams()
    if (adminFilter.trim())     q.set('admin',       adminFilter.trim())
    if (actionFilter)           q.set('action',      actionFilter)
    if (entityTypeFilter.trim()) q.set('entity_type', entityTypeFilter.trim())
    if (entityIdFilter.trim())   q.set('entity_id',   entityIdFilter.trim())
    return q
  }, [adminFilter, actionFilter, entityTypeFilter, entityIdFilter])

  const {
    data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error,
  } = useInfiniteQuery<AuditPage>({
    queryKey: ['audit-log', params.toString()],
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const q = new URLSearchParams(params)
      if (pageParam) q.set('cursor', pageParam as string)
      q.set('limit', '50')
      const res = await api.get(`/audit-log?${q.toString()}`)
      return res.data
    },
    getNextPageParam: (last) => last.next_cursor,
  })

  const { data: actionsList } = useQuery<{ actions: string[] }>({
    queryKey: ['audit-log-actions'],
    queryFn: () => api.get('/audit-log/actions').then(r => r.data),
  })

  const entries = data?.pages.flatMap(p => p.entries) ?? []

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="text-[#C41E3A]" size={22} />
        <h1 className="text-lg font-semibold text-white">Admin audit log</h1>
      </div>
      <p className="text-xs text-gray-500 mb-5 leading-relaxed">
        Every meaningful mutation through the admin portal is recorded here — who
        did what, when, and on which entity, with before/after JSON snapshots.
        Read-only; entries are written by the worker. See <code className="text-gray-400 bg-white/5 px-1 rounded">docs/engineering/security.md</code>{' '}
        for the threat model + instrumented-routes list.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <FilterInput placeholder="Admin id or email" value={adminFilter} onChange={setAdminFilter} />
        <select
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
          className="bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#3a3a3a]"
        >
          <option value="">All actions</option>
          {(actionsList?.actions ?? []).map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <FilterInput placeholder="Entity type (order, user, …)" value={entityTypeFilter} onChange={setEntityTypeFilter} />
        <FilterInput placeholder="Entity id" value={entityIdFilter} onChange={setEntityIdFilter} />
      </div>

      {isLoading && <div className="text-sm text-gray-400">Loading…</div>}
      {error && <div className="text-sm text-red-400">Couldn't load audit log.</div>}

      {!isLoading && entries.length === 0 && (
        <div className="text-sm text-gray-500 bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl px-4 py-8 text-center">
          No audit entries match the current filters.
        </div>
      )}

      {entries.length > 0 && (
        <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                <th className="px-4 py-3 text-left font-medium w-[18%]">When</th>
                <th className="px-4 py-3 text-left font-medium w-[18%]">Admin</th>
                <th className="px-4 py-3 text-left font-medium w-[22%]">Action</th>
                <th className="px-4 py-3 text-left font-medium w-[24%]">Entity</th>
                <th className="px-4 py-3 text-left font-medium">IP</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr
                  key={e.id}
                  onClick={() => setDetail(e)}
                  className="border-b border-[#2e2e2e] last:border-b-0 hover:bg-white/2 cursor-pointer"
                >
                  <td className="px-4 py-2.5 text-gray-300 whitespace-nowrap">{formatDateTime(e.created_at)}</td>
                  <td className="px-4 py-2.5 text-white">
                    {e.admin_name ?? <span className="text-gray-500 italic">(deleted)</span>}
                    {e.admin_email && (
                      <div className="text-[11px] text-gray-500">{e.admin_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <code className="text-xs text-[#C41E3A] bg-white/5 px-1.5 py-0.5 rounded">{e.action}</code>
                  </td>
                  <td className="px-4 py-2.5 text-gray-300">
                    {e.entity_type && (
                      <span className="text-[11px] text-gray-500 mr-1">{e.entity_type}:</span>
                    )}
                    <code className="text-[11px] text-gray-400">{e.entity_id ?? '—'}</code>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{e.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasNextPage && (
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="w-full px-4 py-3 text-xs text-gray-400 hover:text-white border-t border-[#2e2e2e] transition-colors"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}

      {detail && <DetailDrawer entry={detail} onClose={() => setDetail(null)} />}
    </div>
  )
}

function FilterInput({
  placeholder, value, onChange,
}: { placeholder: string; value: string; onChange: (s: string) => void }) {
  return (
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#3a3a3a] placeholder:text-gray-600"
    />
  )
}

function DetailDrawer({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-[#0e0e0e] border-l border-[#2e2e2e] h-full overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">Audit entry</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <Field label="Action">
            <code className="text-[#C41E3A] bg-white/5 px-1.5 py-0.5 rounded">{entry.action}</code>
          </Field>
          <Field label="When">{formatDateTime(entry.created_at)}</Field>
          <Field label="Admin">
            {entry.admin_name ?? <span className="text-gray-500 italic">(deleted)</span>}
            {entry.admin_email && <span className="text-gray-500 ml-1">({entry.admin_email})</span>}
            <div className="text-[11px] text-gray-600 mt-0.5"><code>{entry.admin_id ?? '—'}</code></div>
          </Field>
          <Field label="Entity">
            {entry.entity_type && (
              <span className="text-gray-400">{entry.entity_type}</span>
            )}
            {entry.entity_id && (
              <> · <code className="text-gray-300">{entry.entity_id}</code></>
            )}
          </Field>
          <Field label="IP">{entry.ip ?? <span className="text-gray-500">—</span>}</Field>
          <Field label="User agent">
            <code className="text-[11px] text-gray-400 break-all">{entry.user_agent ?? '—'}</code>
          </Field>
          {entry.before !== null && (
            <Field label="Before">
              <JsonBlock value={entry.before} />
            </Field>
          )}
          {entry.after !== null && (
            <Field label="After">
              <JsonBlock value={entry.after} />
            </Field>
          )}
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-1">{label}</div>
      <div className="text-white">{children}</div>
    </div>
  )
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="text-[11px] text-gray-300 bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg p-3 overflow-x-auto leading-snug">
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}
